import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  createShiftTemplate,
  deleteShiftTemplate,
  listShiftTemplates,
  updateShiftTemplate,
} from '@/services/api.client';
import { extractApiErrorMessage } from '@/lib/api-error';
import type { ShiftTemplate } from '@/types';

const DAYS = [
  { label: 'Sun', value: 0 },
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
];

function formatTimeRange(start: string, end: string) {
  return `${start} \u2013 ${end}`;
}

function formatDays(days: number[]) {
  return days
    .slice()
    .sort((a, b) => a - b)
    .map((d) => DAYS.find((x) => x.value === d)?.label ?? '')
    .join(', ');
}

interface TemplateFormState {
  name: string;
  startTime: string;
  endTime: string;
  daysOfWeek: number[];
}

interface TemplateFormErrors {
  name?: string;
  startTime?: string;
  endTime?: string;
  daysOfWeek?: string;
}

const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

function validateTemplateForm(form: TemplateFormState): TemplateFormErrors {
  const errors: TemplateFormErrors = {};
  if (!form.name.trim()) errors.name = 'Name is required';
  if (!form.startTime) errors.startTime = 'Start time is required';
  else if (!TIME_REGEX.test(form.startTime)) errors.startTime = 'Use HH:MM format (e.g. 06:00)';
  if (!form.endTime) errors.endTime = 'End time is required';
  else if (!TIME_REGEX.test(form.endTime)) errors.endTime = 'Use HH:MM format (e.g. 18:00)';
  if (form.daysOfWeek.length === 0) errors.daysOfWeek = 'Select at least one day';
  return errors;
}

const EMPTY_FORM: TemplateFormState = {
  name: '',
  startTime: '',
  endTime: '',
  daysOfWeek: [],
};

interface Props {
  propertyId: string;
  isPm: boolean;
}

export function ShiftTemplateList({ propertyId, isPm }: Props) {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ShiftTemplate | null>(null);
  const [form, setForm] = useState<TemplateFormState>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<TemplateFormErrors>({});

  const { data: templates, isLoading } = useQuery({
    queryKey: ['shift-templates', propertyId],
    queryFn: () => listShiftTemplates(propertyId),
    enabled: !!propertyId,
  });

  const createMutation = useMutation({
    mutationFn: (body: TemplateFormState) => createShiftTemplate(propertyId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['shift-templates', propertyId] });
      toast.success('Template created');
      setDialogOpen(false);
      setFormErrors({});
    },
    onError: (err: unknown) =>
      toast.error(extractApiErrorMessage(err, 'Failed to create template')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: TemplateFormState }) =>
      updateShiftTemplate(propertyId, id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['shift-templates', propertyId] });
      toast.success('Template updated');
      setDialogOpen(false);
      setEditTarget(null);
      setFormErrors({});
    },
    onError: (err: unknown) =>
      toast.error(extractApiErrorMessage(err, 'Failed to update template')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteShiftTemplate(propertyId, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['shift-templates', propertyId] });
      toast.success('Template deleted');
    },
    onError: (err: unknown) => {
      const status = (err as { status?: number })?.status;
      if (status === 422) {
        toast.error('Cannot delete \u2014 this template has active roster assignments');
      } else {
        toast.error('Failed to delete template');
      }
    },
  });

  function openAdd() {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setDialogOpen(true);
  }

  function openEdit(template: ShiftTemplate) {
    setEditTarget(template);
    setForm({
      name: template.name,
      startTime: template.startTime,
      endTime: template.endTime,
      daysOfWeek: template.daysOfWeek,
    });
    setFormErrors({});
    setDialogOpen(true);
  }

  function toggleDay(day: number) {
    setForm((f) => ({
      ...f,
      daysOfWeek: f.daysOfWeek.includes(day)
        ? f.daysOfWeek.filter((d) => d !== day)
        : [...f.daysOfWeek, day],
    }));
  }

  function handleSubmit() {
    const errors = validateTemplateForm(form);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    if (editTarget) {
      updateMutation.mutate({ id: editTarget.id, body: form });
    } else {
      createMutation.mutate(form);
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-4">
      {isPm && (
        <div className="flex justify-end">
          <Button size="sm" onClick={openAdd}>
            Add Template
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Hours</TableHead>
              <TableHead>Days</TableHead>
              {isPm && <TableHead className="w-24">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates?.map((tpl) => (
              <TableRow key={tpl.id}>
                <TableCell>{tpl.name}</TableCell>
                <TableCell>{formatTimeRange(tpl.startTime, tpl.endTime)}</TableCell>
                <TableCell>{formatDays(tpl.daysOfWeek)}</TableCell>
                {isPm && (
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Edit"
                        onClick={() => openEdit(tpl)}
                      >
                        <Pencil className="size-4" />
                      </Button>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" aria-label="Delete">
                            <Trash2 className="size-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete shift template?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete &ldquo;{tpl.name}&rdquo;. This action
                              cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteMutation.mutate(tpl.id)}>
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editTarget ? 'Edit Template' : 'New Shift Template'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="tpl-name">Name</Label>
              <Input
                id="tpl-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
              {formErrors.name && <p className="text-xs text-destructive">{formErrors.name}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="tpl-start">Start time</Label>
                <Input
                  id="tpl-start"
                  value={form.startTime}
                  onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))}
                  placeholder="HH:MM"
                />
                {formErrors.startTime && (
                  <p className="text-xs text-destructive">{formErrors.startTime}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tpl-end">End time</Label>
                <Input
                  id="tpl-end"
                  value={form.endTime}
                  onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))}
                  placeholder="HH:MM"
                />
                {formErrors.endTime && (
                  <p className="text-xs text-destructive">{formErrors.endTime}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Days of week</Label>
              <div className="flex flex-wrap gap-3">
                {DAYS.map((day) => (
                  <div key={day.value} className="flex items-center gap-1.5">
                    <Checkbox
                      id={`day-${day.value}`}
                      checked={form.daysOfWeek.includes(day.value)}
                      onCheckedChange={() => toggleDay(day.value)}
                    />
                    <Label htmlFor={`day-${day.value}`} className="cursor-pointer font-normal">
                      {day.label}
                    </Label>
                  </div>
                ))}
              </div>
              {formErrors.daysOfWeek && (
                <p className="text-xs text-destructive">{formErrors.daysOfWeek}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isPending}>
              {editTarget ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
